import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import type { Driver } from 'driver.js';
import { createTourDriver } from './tourDriver';
import { tourDefinitions } from './definitions';
import type { TourContextValue, TourState } from './types';
import { getTourIdForPath, TOUR_IDS } from './types';
import { useOrgPreset } from '@/hooks/shared/useOrgPreset';

const STORAGE_KEY = 'mengage-tour-state';

function loadState(): TourState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return { completedTours: [], skippedTours: [], lastStepIndex: {} };
}

function saveState(state: TourState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

const TourContext = createContext<TourContextValue | null>(null);

export function TourProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<TourState>(loadState);
  const [activeTourId, setActiveTourId] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const driverRef = useRef<Driver | null>(null);
  const location = useLocation();
  const autoStartedRef = useRef<Set<string>>(new Set());
  // Org com interface enxuta (preset com `shell`, ex.: Atticus.hub): nada de tour automático. Os tours apresentam o
  // Vertto inteiro e passam por páginas que o preset bloqueia. Ref porque a org chega depois
  // do mount e o timer do tour global é armado uma vez só.
  const orgPreset = useOrgPreset();
  const presetAtivoRef = useRef(false);
  presetAtivoRef.current = Boolean(orgPreset?.shell);

  // Persist state changes
  useEffect(() => {
    saveState(state);
  }, [state]);

  const isTourCompleted = useCallback(
    (tourId: string) =>
      state.completedTours.includes(tourId) ||
      state.skippedTours.includes(tourId),
    [state.completedTours, state.skippedTours]
  );

  const shouldAutoStart = useCallback(
    (tourId: string) => {
      if (presetAtivoRef.current) return false;
      if (isTourCompleted(tourId)) return false;
      if (autoStartedRef.current.has(tourId)) return false;
      // Global tour must complete first before page tours auto-start
      if (
        tourId !== TOUR_IDS.GLOBAL &&
        !isTourCompleted(TOUR_IDS.GLOBAL)
      ) {
        return false;
      }
      return true;
    },
    [isTourCompleted]
  );

  const stopTour = useCallback(() => {
    if (driverRef.current) {
      driverRef.current.destroy();
      driverRef.current = null;
    }
    setActiveTourId(null);
    setIsRunning(false);
  }, []);

  const startTour = useCallback(
    (tourId: string) => {
      const definition = tourDefinitions[tourId];
      if (!definition) return;

      // Stop any running tour
      if (driverRef.current) {
        driverRef.current.destroy();
        driverRef.current = null;
      }

      const driverInstance = createTourDriver(
        {
          onDestroyStarted: () => {
            // Mark current tour as skipped if not completed
            if (!state.completedTours.includes(tourId)) {
              setState((prev) => ({
                ...prev,
                skippedTours: prev.skippedTours.includes(tourId)
                  ? prev.skippedTours
                  : [...prev.skippedTours, tourId],
              }));
            }
            driverInstance.destroy();
          },
          onDestroyed: () => {
            setActiveTourId(null);
            setIsRunning(false);
            driverRef.current = null;
          },
        },
        // onDontShowAgain: skip ALL tours and close immediately
        () => {
          const allTourIds = Object.keys(tourDefinitions);
          setState((prev) => ({
            ...prev,
            skippedTours: allTourIds,
          }));
          driverInstance.destroy();
        }
      );

      // Filter steps that have valid elements in the DOM
      const steps = definition.steps.map((step) => {
        if (!step.element) return step;
        return {
          ...step,
          onHighlightStarted: (element: Element | undefined) => {
            if (!element) {
              // Element not found, skip this step
              driverInstance.moveNext();
            }
            step.popover?.onNextClick;
          },
        };
      });

      // Add onComplete behavior to the last step
      const lastStepIdx = steps.length - 1;
      const originalLastStep = steps[lastStepIdx];
      steps[lastStepIdx] = {
        ...originalLastStep,
        popover: {
          ...originalLastStep.popover,
          onNextClick: () => {
            setState((prev) => ({
              ...prev,
              completedTours: prev.completedTours.includes(tourId)
                ? prev.completedTours
                : [...prev.completedTours, tourId],
              skippedTours: prev.skippedTours.filter((id) => id !== tourId),
            }));
            driverInstance.destroy();
          },
        },
      };

      driverRef.current = driverInstance;
      setActiveTourId(tourId);
      setIsRunning(true);

      // Small delay to ensure DOM elements are present
      setTimeout(() => {
        driverInstance.setSteps(steps);
        driverInstance.drive();
      }, 300);
    },
    [state.completedTours]
  );

  const skipTour = useCallback(
    (tourId: string) => {
      stopTour();
      setState((prev) => ({
        ...prev,
        skippedTours: prev.skippedTours.includes(tourId)
          ? prev.skippedTours
          : [...prev.skippedTours, tourId],
      }));
    },
    [stopTour]
  );

  const restartTour = useCallback(
    (tourId: string) => {
      setState((prev) => ({
        ...prev,
        completedTours: prev.completedTours.filter((id) => id !== tourId),
        skippedTours: prev.skippedTours.filter((id) => id !== tourId),
      }));
      autoStartedRef.current.delete(tourId);
      // Small delay then start
      setTimeout(() => startTour(tourId), 100);
    },
    [startTour]
  );

  const resetAllTours = useCallback(() => {
    stopTour();
    setState({ completedTours: [], skippedTours: [], lastStepIndex: {} });
    autoStartedRef.current.clear();
  }, [stopTour]);

  const getCurrentTourId = useCallback(
    () => getTourIdForPath(location.pathname),
    [location.pathname]
  );

  // Auto-start tour on page navigation
  useEffect(() => {
    if (isRunning) return;

    const tourId = getTourIdForPath(location.pathname);
    if (!tourId) return;
    if (!shouldAutoStart(tourId)) return;

    autoStartedRef.current.add(tourId);

    const timer = setTimeout(() => {
      // Check again after delay (state may have changed)
      if (presetAtivoRef.current) return;
      startTour(tourId);
    }, 1200);

    return () => clearTimeout(timer);
  }, [location.pathname, isRunning, shouldAutoStart, startTour]);

  // Auto-start global tour on first ever visit
  useEffect(() => {
    if (isRunning) return;
    if (!shouldAutoStart(TOUR_IDS.GLOBAL)) return;
    if (autoStartedRef.current.has(TOUR_IDS.GLOBAL)) return;

    autoStartedRef.current.add(TOUR_IDS.GLOBAL);

    const timer = setTimeout(() => {
      if (presetAtivoRef.current) return;
      startTour(TOUR_IDS.GLOBAL);
    }, 2000);

    return () => clearTimeout(timer);
    // Only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<TourContextValue>(
    () => ({
      activeTourId,
      isRunning,
      completedTours: state.completedTours,
      startTour,
      stopTour,
      skipTour,
      restartTour,
      resetAllTours,
      isTourCompleted,
      shouldAutoStart,
      getCurrentTourId,
    }),
    [
      activeTourId,
      isRunning,
      state.completedTours,
      startTour,
      stopTour,
      skipTour,
      restartTour,
      resetAllTours,
      isTourCompleted,
      shouldAutoStart,
      getCurrentTourId,
    ]
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return ctx;
}
